import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import PageCard from "../../components/ui/PageCard";
import MagazinePageHeader from "../magazine/MagazinePageHeader";
import {
  getStatisticsOverview,
  StatisticsOverview,
  StatisticsPeriod,
} from "../../api/statisticsService";
import { useAuth } from "../../hooks/useAuth";
import { canAccessMagazine, canAccessOpsNav } from "../../util/roles";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import ContactsIcon from "@mui/icons-material/Contacts";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { getContacts } from "../../api/contactsService";
import { getProposals } from "../../api/proposalService";

type StatDef = {
  label: string;
  value: string | number;
  to?: string;
  icon: React.ReactNode;
  accent: string;
};

const PERIOD_OPTIONS: { value: StatisticsPeriod; label: string }[] = [
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "1y", label: "1 year" },
  { value: "ytd", label: "Year to date" },
];

const statusTone = (status: string) => {
  const s = (status || "").toLowerCase();
  if (["confirmed", "allowed", "active", "paid", "completed", "signed"].some((k) => s.includes(k))) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }
  if (["pending", "draft", "invited", "submitted", "sent"].some((k) => s.includes(k))) {
    return "bg-amber-50 text-amber-800 ring-amber-600/20";
  }
  if (["cancelled", "canceled", "blocked", "rejected", "failed"].some((k) => s.includes(k))) {
    return "bg-red-50 text-red-700 ring-red-600/20";
  }
  return "bg-gray-100 text-gray-700 ring-gray-500/20";
};

const StatusBadge = ({ status }: { status: string }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${statusTone(status)}`}
  >
    {status || "—"}
  </span>
);

const StatCard = ({ label, value, to, icon, accent }: StatDef) => {
  const body = (
    <PageCard className="!p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="mt-2 truncate text-3xl font-semibold tracking-tight text-gray-900">
            {value}
          </p>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${accent}`}>
          {icon}
        </div>
      </div>
    </PageCard>
  );

  if (to) {
    return (
      <Link to={to} className="block no-underline">
        {body}
      </Link>
    );
  }
  return body;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
    {children}
  </h2>
);

const SkeletonBlock = () => (
  <div className="animate-pulse space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-28 rounded-md bg-white shadow-[5px_5px_15px_-3px_#BCCCDC]" />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="h-48 rounded-md bg-white shadow-[5px_5px_15px_-3px_#BCCCDC]" />
      <div className="h-48 rounded-md bg-white shadow-[5px_5px_15px_-3px_#BCCCDC]" />
    </div>
  </div>
);

const StatusList = ({ title, data, to }: { title: string; data: Record<string, number>; to?: string }) => {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);

  if (!entries.length) {
    return (
      <PageCard>
        <h3 className="mb-3 text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">No data yet.</p>
      </PageCard>
    );
  }

  return (
    <PageCard>
      <div className="mb-4 flex items-center justify-between gap-3"><h3 className="text-lg font-semibold text-gray-900">{title}</h3>{to && <Link to={to} className="text-sm font-medium text-[#5949d1] no-underline hover:underline">View all</Link>}</div>
      <ul className="space-y-3">
        {entries.map(([status, count]) => {
          const pct = total ? Math.round((count / total) * 100) : 0;
          return (
            <li key={status} className="list-none"><Link to={to ? `${to}?status=${encodeURIComponent(status)}` : "#"} className="block rounded p-1 no-underline hover:bg-gray-50">
              <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                <StatusBadge status={status} />
                <span className="font-semibold text-gray-900">{count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-[#5949d1]" style={{ width: `${pct}%` }} />
              </div>
            </Link></li>
          );
        })}
      </ul>
    </PageCard>
  );
};

type RecentRow = {
  id: number;
  cells: React.ReactNode[];
  href?: string;
};

const RecentTable = ({
  title,
  columns,
  rows,
}: {
  title: string;
  columns: string[];
  rows: RecentRow[];
}) => (
  <PageCard className="overflow-x-auto">
    <h3 className="mb-4 text-lg font-semibold text-gray-900">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-500">No recent activity.</p>
    ) : (
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500">
            {columns.map((col) => (
              <th key={col} className="pb-2 pr-4 font-medium">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/80">
              {row.cells.map((cell, j) => (
                <td key={j} className="py-2.5 pr-4 text-gray-800">
                  {row.href && j === 0 ? (
                    <Link
                      to={row.href}
                      className="font-medium text-[#5949d1] no-underline hover:underline"
                    >
                      {cell}
                    </Link>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </PageCard>
);

const formatDate = (value: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (amount: number) =>
  `£${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const iconClass = "text-xl text-white";

const Dashboard = () => {
  const { user, isAdmin, isSalesManager, canAccessCompanies } = useAuth();
  const showOps = canAccessOpsNav(user);
  const magazineAccess = canAccessMagazine(user);

  const [period, setPeriod] = useState<StatisticsPeriod>("1m");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StatisticsOverview | null>(null);
  const [reminders, setReminders] = useState<Array<{ id: string; title: string; detail: string; remindAt: string; href: string }>>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [res, contacts, proposals] = await Promise.all([getStatisticsOverview(period), getContacts(), getProposals()]);
    setData(res);
    const upcoming = [
      ...contacts.filter((c: any) => c.remind_at).map((c: any) => ({ id: `contact-${c.id}`, title: [c.first_name, c.last_name].filter(Boolean).join(" "), detail: c.company?.name || "Contact follow-up", remindAt: c.remind_at, href: `/crm/contacts/${c.id}` })),
      ...proposals.filter((p) => p.remind_at).map((p) => ({ id: `proposal-${p.id}`, title: p.contact_name || p.company?.name || `Proposal #${p.id}`, detail: `${p.company?.name || "Proposal"} · Proposal reminder`, remindAt: p.remind_at!, href: `/magazine/proposals/${p.id}` })),
    ].filter((item) => new Date(item.remindAt).getTime() >= Date.now()).sort((a, b) => new Date(a.remindAt).getTime() - new Date(b.remindAt).getTime()).slice(0, 8);
    setReminders(upcoming);
    setLoading(false);
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = data?.counts;
  const sales = data?.sales;

  const salesStats: StatDef[] = sales
    ? [
        ...(magazineAccess
          ? [
              {
                label: data?.isPersonalView ? "My magazine sales" : "Magazine sales (signed)",
                value: formatMoney(sales.magazineSignedRevenue),
                to: "/magazine/proposals?status=signed",
                icon: <BookOnlineIcon className={iconClass} />,
                accent: "bg-pink-600",
              },
              {
                label: data?.isPersonalView ? "My signed proposals" : "Signed proposals",
                value: sales.magazineSignedCount,
                to: "/magazine/proposals?status=signed",
                icon: <MenuBookIcon className={iconClass} />,
                accent: "bg-fuchsia-600",
              },
            ]
          : []),
        ...(showOps
          ? [
              {
                label: "Event order revenue",
                value: formatMoney(sales.eventOrderRevenue),
                to: "/orders",
                icon: <AttachMoneyIcon className={iconClass} />,
                accent: "bg-emerald-600",
              },
              {
                label: "Event orders",
                value: sales.eventOrderCount,
                to: "/orders",
                icon: <ShoppingCartIcon className={iconClass} />,
                accent: "bg-orange-600",
              },
            ]
          : []),
      ]
    : [];

  const opsStats: StatDef[] =
    counts && showOps
      ? [
          {
            label: "Events",
            value: counts.events,
            to: "/events",
            icon: <EventIcon className={iconClass} />,
            accent: "bg-violet-600",
          },
          {
            label: "Exhibitors",
            value: counts.exhibitors,
            to: "/applicants",
            icon: <AssignmentIcon className={iconClass} />,
            accent: "bg-indigo-600",
          },
          {
            label: "Companies",
            value: counts.companies,
            to: "/companys",
            icon: <BusinessIcon className={iconClass} />,
            accent: "bg-sky-600",
          },
          ...(isAdmin && counts.contacts != null
            ? [
                {
                  label: "Contacts",
                  value: counts.contacts,
                  to: "/crm/contacts",
                  icon: <ContactsIcon className={iconClass} />,
                  accent: "bg-cyan-600",
                } as StatDef,
              ]
            : []),
        ]
      : [];

  const crmStats: StatDef[] =
    counts && !showOps && canAccessCompanies
      ? [
          ...(canAccessCompanies
            ? [
                {
                  label: "Companies",
                  value: counts.companies,
                  to: "/companys",
                  icon: <BusinessIcon className={iconClass} />,
                  accent: "bg-sky-600",
                } as StatDef,
              ]
            : []),
        ]
      : [];

  const magazineCountStats: StatDef[] =
    counts && magazineAccess
      ? [
          {
            label: "Publications",
            value: counts.publications,
            to: "/magazine/publications",
            icon: <MenuBookIcon className={iconClass} />,
            accent: "bg-fuchsia-600",
          },
          {
            label: "Active proposals",
            value: counts.magazineBookings,
            to: "/magazine/proposals",
            icon: <BookOnlineIcon className={iconClass} />,
            accent: "bg-pink-600",
          },
        ]
      : [];

  const showMyMagazineRecent = isSalesManager;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl pb-8">
        <MagazinePageHeader
          title="Dashboard"
          description={
            data?.isPersonalView
              ? "Your sales performance and recent activity."
              : "Overview from your CRM, events, and magazine data."
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
                value={period}
                onChange={(e) => setPeriod(e.target.value as StatisticsPeriod)}
                disabled={loading}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshIcon fontSize="small" />
                Refresh
              </button>
            </div>
          }
        />

        {loading && <SkeletonBlock />}

        {!loading && !data && (
          <PageCard>
            <p className="text-sm text-red-600">Could not load dashboard.</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-sm font-medium text-[#5949d1] hover:underline"
            >
              Try again
            </button>
          </PageCard>
        )}

        {!loading && data && counts && (
          <div className="space-y-8">
            <section>
              <SectionTitle>Reminders</SectionTitle>
              <PageCard>
                <div className="mb-4 flex items-center gap-2"><NotificationsActiveOutlinedIcon className="text-amber-600" /><h3 className="text-lg font-semibold text-gray-900">Next calls &amp; follow-ups</h3></div>
                {reminders.length === 0
                  ? <p className="text-sm text-gray-500">No upcoming reminders.</p>
                  : <ul className="divide-y divide-gray-100">
                    {reminders.map((reminder) => 
                      <li className="list-none" key={reminder.id}>
                        <Link
                          to={reminder.href}
                          className="flex items-center justify-between gap-4 rounded py-3 no-underline px-2 py-1 hover:bg-gray-50"
                        >
                          <div>
                            <p className="font-medium text-[#5949d1]">
                              {reminder.title}
                            </p>
                            <p className="mt-0.5 text-sm text-gray-500">
                              {reminder.detail}
                            </p>
                          </div>
                          <time className="shrink-0 text-sm font-medium text-amber-700">
                            {new Date(reminder.remindAt).toLocaleString()}
                          </time>
                        </Link>
                      </li>
                    )}
                  </ul>
                }
              </PageCard>
            </section>
            {salesStats.length > 0 && (
              <section>
                <SectionTitle>Sales ({PERIOD_OPTIONS.find((p) => p.value === period)?.label})</SectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {salesStats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>
              </section>
            )}

            {opsStats.length > 0 && (
              <section>
                <SectionTitle>Events &amp; CRM</SectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {opsStats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>
              </section>
            )}

            {crmStats.length > 0 && (
              <section>
                <SectionTitle>CRM</SectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {crmStats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>
              </section>
            )}

            {magazineCountStats.length > 0 && (
              <section>
                <SectionTitle>Magazine</SectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {magazineCountStats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                  ))}
                </div>
              </section>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {showOps && (
                <StatusList title="Exhibitors by status" data={data.exhibitorByStatus} />
              )}
              {magazineAccess && (
                <StatusList title="Proposals by status" data={data.magazineByStatus} to="/magazine/proposals" />
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {showOps && (
                <RecentTable
                  title="Recent exhibitors"
                  columns={["Company", "Event", "Status", "Date"]}
                  rows={data.recent.exhibitors.map((row) => ({
                    id: row.id,
                    href: `/applicants/${row.id}`,
                    cells: [
                      row.company,
                      row.event,
                      <StatusBadge key="s" status={row.status} />,
                      formatDate(row.createdAt),
                    ],
                  }))}
                />
              )}
              {magazineAccess && (
                <RecentTable
                  title={isSalesManager ? "Recent magazine (all)" : "Recent proposals"}
                  columns={["Company", "Detail", "Status", "Date"]}
                  rows={data.recent.magazineBookings.map((row) => ({
                    id: row.id,
                    href: row.href,
                    cells: [
                      row.company,
                      row.detail,
                      <StatusBadge key="s" status={row.status} />,
                      formatDate(row.createdAt),
                    ],
                  }))}
                />
              )}
              {showMyMagazineRecent && (
                <RecentTable
                  title="My recent magazine"
                  columns={["Company", "Detail", "Status", "Date"]}
                  rows={data.recent.myMagazineBookings.map((row) => ({
                    id: row.id,
                    href: row.href,
                    cells: [
                      row.company,
                      row.detail,
                      <StatusBadge key="s" status={row.status} />,
                      formatDate(row.createdAt),
                    ],
                  }))}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
